import { useId, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronDown } from "lucide-react"
import type { ClientTheme } from "@/app/lib/themes"
import { usePopover } from "@/app/hooks/usePopover"
import { normalizeStr } from "@/app/lib/str"

interface StrComboboxProps {
  value: string
  onSelect: (str: string) => void
  options: string[] // owned (DIV) or statewide (ACQ)
  allowCreate: boolean // ACQ = true, DIV = false
  theme: ClientTheme
  invalid?: boolean
}

const MAX_RENDERED = 50

/**
 * Filterable, keyboard-accessible STR picker for the multi-transaction grid.
 * Mirrors SelectMenu's portal + theme styling but adds ArrowUp/ArrowDown/Enter
 * navigation and (for ACQ rows) free entry of a new valid STR via `allowCreate`.
 * DIV rows pass owned STRs and are not creatable; ACQ rows pass statewide STRs
 * and can commit a normalized new STR on Enter or blur.
 */
export function StrCombobox({
  value,
  onSelect,
  options,
  allowCreate,
  theme,
  invalid,
}: StrComboboxProps) {
  const { open, setOpen, triggerRef, popoverRef, popoverStyle } = usePopover<HTMLDivElement>()
  const [query, setQuery] = useState("")
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()
  const optId = (i: number) => `${listId}-opt-${i}`

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = q === "" ? options : options.filter((o) => o.toLowerCase().includes(q))
    return list.slice(0, MAX_RENDERED)
  }, [options, query])

  const normalizedQuery = normalizeStr(query)
  const canCreate =
    allowCreate &&
    query.trim() !== "" &&
    normalizedQuery !== null &&
    !options.includes(normalizedQuery)

  const commit = (str: string) => {
    const canonical = normalizeStr(str) ?? str.trim()
    onSelect(canonical)
    setOpen(false)
    setQuery("")
  }

  const rows = filtered
  const total = rows.length + (canCreate ? 1 : 0)

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActive((a) => Math.min(a + 1, total - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (canCreate && active === rows.length) commit(normalizedQuery!)
      else if (rows[active]) commit(rows[active])
    } else if (e.key === "Escape") {
      setOpen(false)
    }
  }

  const onBlur = () => {
    // Free-entry commit on blur for ACQ when the text is a valid new STR.
    if (canCreate) commit(normalizedQuery!)
  }

  return (
    <div ref={triggerRef} className="relative" style={{ width: "100%" }}>
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open ? optId(active) : undefined}
        value={open ? query : value}
        placeholder={value || "14-13N-19W"}
        onFocus={() => {
          setOpen(true)
          setQuery("")
          setActive(0)
        }}
        onChange={(e) => {
          setQuery(e.target.value)
          setActive(0)
          setOpen(true)
        }}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        style={{
          width: "100%",
          padding: "8px 28px 8px 11px",
          borderRadius: "8px",
          backgroundColor: theme.bgSurface2,
          border: `1px solid ${invalid ? theme.brandAccent : theme.borderColor}`,
          color: theme.textPrimary,
          fontFamily: "var(--font-ui)",
          fontSize: "13px",
          outline: "none",
        }}
      />
      <ChevronDown
        size={12}
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: theme.textDim,
          pointerEvents: "none",
        }}
      />

      {open &&
        popoverStyle &&
        createPortal(
          <div
            id={listId}
            role="listbox"
            ref={popoverRef}
            onMouseDown={(e) => e.preventDefault()}
            className="rounded-lg overflow-hidden"
            style={{
              ...popoverStyle,
              zIndex: 60,
              overflowY: "auto",
              backgroundColor: theme.bgSurface,
              border: `1px solid ${theme.borderColor}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {rows.length === 0 && !canCreate && (
              <div
                className="px-3 py-3"
                style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: theme.textDim }}
              >
                No matching STRs
              </div>
            )}
            {rows.map((o, i) => (
              <button
                key={o}
                id={optId(i)}
                role="option"
                aria-selected={i === active}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(o)}
                onMouseEnter={() => setActive(i)}
                className="flex w-full px-3 py-2 text-left"
                style={{
                  background: i === active ? theme.bgSurface2 : "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "var(--font-ui)",
                  fontSize: 12,
                  color: theme.textPrimary,
                }}
              >
                {o}
              </button>
            ))}
            {canCreate && (
              <button
                id={optId(rows.length)}
                role="option"
                aria-selected={active === rows.length}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(normalizedQuery!)}
                onMouseEnter={() => setActive(rows.length)}
                className="flex w-full px-3 py-2 text-left"
                style={{
                  background: active === rows.length ? theme.bgSurface2 : "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "var(--font-ui)",
                  fontSize: 12,
                  color: theme.brandPrimary,
                }}
              >
                Add &ldquo;{normalizedQuery}&rdquo;
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  )
}
